import { ONBOARDING_ADD_METHOD } from '#~/analytics/constants/onboarding-events.js';
import { Protocol } from '@studio/models/control';
import { VideoDefaults } from '@studio/models/video';
import { RtspDefaults } from '@studio/models/video/rtsp';
import { defaults } from 'lodash-es';
import { isTransient } from '@studio/models/device';
import { useLazyRef, χref } from '@studio/react-utils/hooks';
import { freeze } from 'immer';
import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { describeOnvifObservation, traceCameraAdd } from '#~/device/add-trace.js';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';
import { Context } from './context.js';
import type { Draft, Props, State, Store } from './types.js';
import { Panel } from './types.js';
import { isDiscoveryInUse } from './utils.js';
import type { Discovery$Control, Discovery$Video } from '#~/device/discovery/index.js';
import { isControlDiscovery, isVideoDiscovery } from '#~/device/discovery/index.js';
import { isVideoDevice } from '#~/device/index.js';
import { generateFlowId } from '#~/analytics/pageview/index.js';
import type { Device } from '@studio/models/device';

/** Add video source workflow `<Provider>`. */
export const Provider = ({
  agentIds,
  appId,
  children,
  devices,
  discoveries,
  onboarding,
  roomId
}: Props): ReactElement => {
  const previousOnvifObservations = useRef(new Map<Device['id'], string>());
  const store = useLazyRef<Store>(() =>
    create<State>()(
      immer((set) =>
        freeze({
          agentId: agentIds[0]!,
          agentIds,
          appId,
          ...(onboarding?.initialPanel === Panel.Video
            ? { addMethod: ONBOARDING_ADD_METHOD.MANUAL }
            : {}),
          // Skip-credentials: no skip until the operator clicks Skip (impl-spec §4).
          authSkipped: false,
          // Opening ON the video panel means nothing selected a source, so the
          // candidate needs the same RTSP seed the Discover footer's
          // "Manually Configure" link applies (source/footer.tsx
          // handleManualRtsp) — otherwise the panel opens on an empty
          // configuration with no protocol and renders nothing.
          candidate:
            onboarding?.initialPanel === Panel.Video
              ? { video: defaults({ ...RtspDefaults }, VideoDefaults) }
              : {},
          control: {
            cache: {},
            isReady: false
          },
          external: {
            transients: {}
          },
          flowId: generateFlowId(),
          manufacturer: {
            isReady: false,
            preferredModelId: {}
          },
          // Seeded once, from the caller. Undefined for every caller but the
          // first-run funnel, which is what makes the onboarding branches
          // unreachable from the normal Add Video Source flow.
          onboarding,
          // Onboarding's "Enter manually" opens straight on the video panel.
          // Every other caller starts at Discover, as before.
          panel: onboarding?.initialPanel ?? Panel.Source,
          previews: {},
          previewGeneration: 0,
          previewAvailability: 'idle',
          previewCopy: undefined,
          previewPending: false,
          // No preview add in flight; the Mediator records each one it sends.
          pendingPreviews: {},
          produce: (fn: (draft: Draft) => void): void => {
            set(fn);
          },
          roomId,
          source: {
            // Bulk-add state initialized inactive.
            bulkMode: false,
            // Bulk commit pipeline starts idle; transitions through
            // preparing → configuring → (awaiting-auth)* → committing
            // → done driven by `runBulkCommit` in wiring.ts.
            bulkAddStatus: { kind: 'idle' },
            // Cancellation flag (Multi-Add Phase 2 §2.10) starts false.
            // `handleBulkCommit` clears it again at
            // the start of every batch so a residual flag from a prior
            // batch (set in the half-second between the Cancel click and
            // the next "Add N Sources" click) cannot short-circuit the
            // new run.
            cancellationRequested: false,
            discoveries: [],
            existing: [],
            // Failed-device slice (Multi-Add Phase 2 §2.11). Devices
            // that failed mid-bulk-commit accumulate here so the
            // failed-tile
            // placeholder can render them. Slice scope is per-room; the
            // Provider's room-leave unmount clears the whole store, so no
            // explicit cleanup needed beyond initialising empty.
            failedDevices: [],
            isReady: false,
            multiSelection: [],
            // Brand-level fingerprint seed (Change 2B) starts unset; set by
            // panel.tsx when a brand-only discovery row is selected, read by
            // rtsp.tsx on mount to pre-seed the Brand dropdown.
            seededBrandId: undefined,
            // bug1: brand-only host seed (set by panel.tsx, read by rtsp.tsx to
            // pre-fill the IP without syncing a connectable video aspect).
            seededHost: undefined,
            // No RTSP (ONVIF) probe attempt yet; discovery.tsx starts one per row or login.
            onvifProbe: undefined,
            selection: undefined
          },
          video: {
            cache: {},
            isReady: false,
            // The empty-creds probe raises this while in flight; panel.tsx folds
            // `!probing` into its computed video.isReady.
            probing: false
          }
        })
      )
    )
  );

  // Inject external state.
  useEffect(() => {
    const { produce } = χref(store).getState();

    // Compare both flows at the shared renderer boundary without logging every state-sync tick.
    const nextObservations = new Map<Device['id'], string>();
    for (const device of Object.values(devices)) {
      if (!isTransient(device.id) || device.control?.config.protocol !== Protocol.ONVIF) continue;
      const observation = describeOnvifObservation(devices, device.id);
      const signature = JSON.stringify(observation);
      nextObservations.set(device.id, signature);
      if (previousOnvifObservations.current.get(device.id) !== signature) {
        traceCameraAdd('renderer.onvif-observation', {
          deviceId: device.id,
          agentId: device.agentId,
          observation
        });
      }
    }
    for (const deviceId of previousOnvifObservations.current.keys()) {
      if (!nextObservations.has(deviceId)) traceCameraAdd('renderer.onvif-removed', { deviceId });
    }
    previousOnvifObservations.current = nextObservations;

    produce(({ external, source }) => {
      // Keep track of existing video devices.
      source.existing = freeze(
        Object.values(devices)
          .filter((µ) => !isTransient(µ.id))
          .filter(isVideoDevice)
      );

      // Keep track of discovered video devices.
      source.discoveries = freeze(
        Object.values(discoveries)
          .filter(
            (µ): µ is Discovery$Control | Discovery$Video =>
              isVideoDiscovery(µ) ||
              (isControlDiscovery(µ) && µ.control.protocol === Protocol.ONVIF)
          )
          .filter(
            (discovery) =>
              // Filter out discoveries that are already in use.
              !source.existing.some((device) => isDiscoveryInUse(device, discovery))
          )
      );

      // Keep track of the status of transient devices.
      external.transients = freeze(
        Object.fromEntries(
          Object.values(devices)
            .filter((µ) => isTransient(µ.id))
            .map((µ) => [µ.id, µ])
        )
      );
    });
  }, [devices, discoveries]);

  // Sync room-scoped props when they change — the modal is shared across
  // rooms, so agentIds/roomId would otherwise go stale.
  useEffect(() => {
    const { produce } = χref(store).getState();

    produce((draft) => {
      draft.agentIds = agentIds;
      draft.roomId = roomId;

      // Reset agentId if it's no longer valid in the new agentIds list.
      if (!agentIds.includes(draft.agentId)) {
        draft.agentId = agentIds[0]!;
      }
    });
  }, [agentIds, roomId]);

  return (
    <Context.Provider key='context-provider' value={χref(store)}>
      {children}
    </Context.Provider>
  );
};
